/**
 * DeleteConfirmDialog — Phase 6a.22a (introduced) + Phase 6e.4.2-hotfix-2
 * (extracted to its own file, shared by ContractListPage and
 * ContractEntryPage).
 *
 * Confirm modal that gates contract deletion. Component shape unchanged
 * from the inline version that lived in ContractListPage; downstream
 * action moves from soft-delete + undo to hard-delete + toast as part
 * of hotfix-2 Bug D, but that change is the caller's concern.
 */

import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { displayNameFor } from "@/features/engagement/displayName";
import type { EngagementHistoryEntry } from "@/types";

export interface DeleteConfirmDialogProps {
  engagement: EngagementHistoryEntry;
  pendingDeleteIndex: number | null;
  onCancel: () => void;
  onConfirm: () => void;
}

export function DeleteConfirmDialog({
  engagement,
  pendingDeleteIndex,
  onCancel,
  onConfirm,
}: DeleteConfirmDialogProps) {
  const { t } = useTranslation();
  const open = pendingDeleteIndex !== null;
  const slot =
    pendingDeleteIndex !== null
      ? engagement.contracts[pendingDeleteIndex] ?? null
      : null;
  const displayName = displayNameFor(
    slot,
    t("engagement.list.emptySlotName"),
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent data-testid="contract-delete-confirm-dialog">
        <DialogHeader>
          <DialogTitle>
            {t("engagement.contract.delete.confirmTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("engagement.contract.delete.confirmDescription", {
              index: (pendingDeleteIndex ?? 0) + 1,
              name: displayName,
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="contract-delete-confirm-cancel"
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            data-testid="contract-delete-confirm-confirm"
          >
            {t("engagement.contract.delete.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
